import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { apiFetch } from '../../api/client';

const EMPTY_FORM = {
  tipoAditivo: '',
  justificativa: '',
  vigenciaInicio: '',
  vigenciaFim: '',
  valorMensal: '',
  valorTotal: '',
  descricaoEscopo: '',
  observacoes: '',
};

/**
 * Dialog de aditivo da solicitação (minuta_json + tipo/justificativa).
 */
export default function SolicitacaoAditivoDialog({ open, item, onClose, onSaved, onError }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!open || !item?.id) return;
    setLoading(true);
    setForm(EMPTY_FORM);
    apiFetch(`/api/contratos/solicitacoes/${item.id}/aditivo`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const minuta = data?.minutaJson && typeof data.minutaJson === 'object' ? data.minutaJson : {};
        if (data) {
          setForm({
            tipoAditivo: data.tipoAditivo || '',
            justificativa: data.justificativa || '',
            vigenciaInicio: minuta.vigenciaInicio || '',
            vigenciaFim: minuta.vigenciaFim || '',
            valorMensal: minuta.valorMensal != null ? String(minuta.valorMensal) : '',
            valorTotal: minuta.valorTotal != null ? String(minuta.valorTotal) : '',
            descricaoEscopo: minuta.descricaoEscopo || '',
            observacoes: minuta.observacoes || '',
          });
        }
      })
      .catch(() => onError?.('Erro ao carregar aditivo.'))
      .finally(() => setLoading(false));
  }, [open, item?.id, onError]);

  const patch = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const salvar = async () => {
    if (!item?.id) return;
    if (!form.tipoAditivo?.trim()) {
      onError?.('Selecione o tipo de aditivo.');
      return;
    }
    setSaving(true);
    try {
      const minutaJson = {
        vigenciaInicio: form.vigenciaInicio || null,
        vigenciaFim: form.vigenciaFim || null,
        valorMensal: form.valorMensal ? Number(String(form.valorMensal).replace(',', '.')) : null,
        valorTotal: form.valorTotal ? Number(String(form.valorTotal).replace(',', '.')) : null,
        descricaoEscopo: form.descricaoEscopo || null,
        observacoes: form.observacoes || null,
      };
      const res = await apiFetch(`/api/contratos/solicitacoes/${item.id}/aditivo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipoAditivo: form.tipoAditivo || null,
          justificativa: form.justificativa || null,
          minutaJson,
          status: 'RASCUNHO',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Erro ao salvar aditivo');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      onError?.(e.message || 'Erro ao salvar aditivo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Aditivo — {item?.codigo}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          Contrato: {item?.contrato?.numero} {item?.contrato?.empresa}
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Tipo de aditivo</InputLabel>
              <Select
                value={form.tipoAditivo || ''}
                label="Tipo de aditivo"
                onChange={(e) => patch('tipoAditivo', e.target.value)}
              >
                <MenuItem value="">Selecione</MenuItem>
                <MenuItem value="PRAZO">Prazo</MenuItem>
                <MenuItem value="VALOR">Valor</MenuItem>
                <MenuItem value="ESCOPO">Escopo</MenuItem>
                <MenuItem value="REAJUSTE">Reajuste</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Justificativa"
              multiline
              rows={3}
              fullWidth
              value={form.justificativa || ''}
              onChange={(e) => patch('justificativa', e.target.value)}
              placeholder="Descreva a justificativa para o aditivo..."
            />
            {(form.tipoAditivo === 'PRAZO' || form.tipoAditivo === 'REAJUSTE' || !form.tipoAditivo) && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nova vigência — início"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={form.vigenciaInicio || ''}
                    onChange={(e) => patch('vigenciaInicio', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nova vigência — fim"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={form.vigenciaFim || ''}
                    onChange={(e) => patch('vigenciaFim', e.target.value)}
                  />
                </Grid>
              </Grid>
            )}
            {(form.tipoAditivo === 'VALOR' || form.tipoAditivo === 'REAJUSTE') && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valor mensal (R$)"
                    fullWidth
                    size="small"
                    value={form.valorMensal || ''}
                    onChange={(e) => patch('valorMensal', e.target.value)}
                    placeholder="Ex: 15000.00"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valor total (R$)"
                    fullWidth
                    size="small"
                    value={form.valorTotal || ''}
                    onChange={(e) => patch('valorTotal', e.target.value)}
                    placeholder="Ex: 180000.00"
                  />
                </Grid>
              </Grid>
            )}
            {(form.tipoAditivo === 'ESCOPO' || form.tipoAditivo === 'VALOR') && (
              <TextField
                label="Descrição do escopo / alteração"
                multiline
                rows={3}
                fullWidth
                value={form.descricaoEscopo || ''}
                onChange={(e) => patch('descricaoEscopo', e.target.value)}
                placeholder="Detalhe o que muda no objeto/escopo do contrato..."
              />
            )}
            <TextField
              label="Observações"
              multiline
              rows={2}
              fullWidth
              value={form.observacoes || ''}
              onChange={(e) => patch('observacoes', e.target.value)}
              placeholder="Notas internas da minuta (opcional)"
            />
            <Alert severity="info">
              Os campos extras são gravados na minuta do aditivo e ficam disponíveis no processo.
            </Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Fechar
        </Button>
        <Button
          variant="contained"
          onClick={salvar}
          disabled={loading || saving || !form.tipoAditivo}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Salvando...' : 'Salvar aditivo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
